import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import {
  Users, Plus, ArrowLeft, Crown, Settings, Activity, BarChart3,
  CheckCircle, Circle, MapPin, Compass, Star, Target, Heart,
  MessageCircle, Calendar, TrendingUp, Trash2, LogOut, UserMinus
} from "lucide-react";

const CATEGORIES = {
  travel: { icon: MapPin, color: "bg-primary", label: "Travel" },
  adventures: { icon: Compass, color: "bg-secondary", label: "Adventures" },
  experiences: { icon: Star, color: "bg-accent", label: "Experiences" },
  goals: { icon: Target, color: "bg-charcoal", label: "Goals" },
  romance: { icon: Heart, color: "bg-coral", label: "Romance" },
};

export const GroupView = ({ group, isOpen, onClose, onAddItem }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("items");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchGroupData = async () => {
    if (!group) return;
    
    setLoading(true);
    try {
      const [itemsRes, activityRes, statsRes] = await Promise.all([
        axios.get(`${API}/groups/${group.id}/items`),
        axios.get(`${API}/groups/${group.id}/activity`),
        axios.get(`${API}/groups/${group.id}/stats`)
      ]);
      
      setItems(itemsRes.data);
      setActivity(activityRes.data.activity);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch group data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && group) {
      fetchGroupData();
    }
  }, [isOpen, group]);

  const handleToggleComplete = async (item) => {
    try {
      await axios.post(`${API}/items/${item.id}/toggle-complete`);
      toast.success(item.completed ? "Marked as pending" : "Completed! 🎉");
      fetchGroupData();
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const handleDeleteGroup = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/groups/${group.id}`);
      toast.success("Group deleted successfully");
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete group");
    } finally {
      setDeleting(false);
    }
  };

  const handleLeaveGroup = async () => {
    try {
      await axios.post(`${API}/groups/${group.id}/leave`);
      toast.success("You have left the group");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave group");
    }
  };

  const isAdmin = group?.members?.find(m => m.id === user.id)?.role === "admin";

  if (!group) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] bg-card border-none rounded-2xl p-0 max-h-[90vh] overflow-hidden">
        {/* !text-left overrides shadcn's default mobile text-center which
            pushed the subtitle off-axis. pr-12 keeps the title block clear
            of the modal's built-in close X. */}
        <DialogHeader className="p-4 sm:p-6 pb-3 sm:pb-4 pr-12 sm:pr-14 border-b border-charcoal/5 !text-left">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0 h-8 w-8">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-serif text-lg sm:text-xl text-charcoal flex items-center gap-2 truncate">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0" />
                <span className="truncate">{group.name}</span>
                {isAdmin && <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-accent shrink-0" />}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {group.members?.length} members • {items.length} items
              </DialogDescription>
            </div>
          </div>
          {/* Add Item — full-width primary CTA on its own row. Living below
              the title row (instead of inline-right) keeps it clear of the
              modal's built-in close X on mobile and gives the button a
              proper hit target. */}
          <Button
            onClick={() => {
              onAddItem(group);
              onClose();
            }}
            className="mt-3 w-full bg-primary text-white hover:bg-primary/90 rounded-full h-10 text-sm font-medium"
            data-testid="add-group-item-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Item to {group.name}
          </Button>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <div className="px-3 sm:px-6 pt-2 border-b border-charcoal/5 overflow-x-auto">
            <TabsList className="bg-transparent gap-1 sm:gap-2 h-10 min-w-max">
              <TabsTrigger 
                value="items" 
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-2.5 sm:px-4 text-xs sm:text-sm"
              >
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Items ({items.length})
              </TabsTrigger>
              <TabsTrigger 
                value="members"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-2.5 sm:px-4 text-xs sm:text-sm"
              >
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Members
              </TabsTrigger>
              <TabsTrigger 
                value="activity"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-2.5 sm:px-4 text-xs sm:text-sm"
              >
                <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Activity
              </TabsTrigger>
              <TabsTrigger 
                value="stats"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-2.5 sm:px-4 text-xs sm:text-sm"
              >
                <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Stats
              </TabsTrigger>
              <TabsTrigger 
                value="settings"
                className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-full px-2.5 sm:px-4 text-xs sm:text-sm"
              >
                <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1 max-h-[60vh] [&>[data-radix-scroll-area-viewport]>div]:!block">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : (
              <>
                {/* Items Tab */}
                <TabsContent value="items" className="p-6 m-0">
                  {items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                      <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                        <Star className="w-8 h-8 text-secondary" />
                      </div>
                      <h3 className="font-medium text-charcoal mb-2">No items yet</h3>
                      <p className="text-sm text-muted-foreground mb-4 max-w-xs">
                        Add the first bucket list item for your group
                      </p>
                      <Button
                        onClick={() => {
                          onAddItem(group);
                          onClose();
                        }}
                        size="sm"
                        className="bg-primary text-white hover:bg-primary/90 rounded-full"
                        data-testid="empty-state-add-item-btn"
                      >
                        <Plus className="w-4 h-4 mr-1.5" />
                        Add first item
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {items.map((item) => {
                        const category = CATEGORIES[item.category] || CATEGORIES.experiences;
                        const CategoryIcon = category.icon;
                        
                        return (
                          <div
                            key={item.id}
                            className={`p-4 rounded-xl border border-charcoal/5 bg-background ${
                              item.completed ? "opacity-70" : ""
                            }`}
                            data-testid={`group-item-${item.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full ${category.color} bg-opacity-20 flex items-center justify-center shrink-0`}>
                                <CategoryIcon className="w-5 h-5 text-current" style={{ color: category.color.replace("bg-", "") }} />
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-medium text-charcoal ${item.completed ? "line-through" : ""}`}>
                                  {item.title}
                                </h4>
                                {item.description && (
                                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                    {item.description}
                                  </p>
                                )}
                                
                                <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                                  <span>by {item.created_by_name}</span>
                                  {item.target_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {format(new Date(item.target_date), "MMM d")}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleToggleComplete(item)}
                                className={item.completed ? "text-secondary" : "text-muted-foreground"}
                              >
                                {item.completed ? (
                                  <CheckCircle className="w-5 h-5" />
                                ) : (
                                  <Circle className="w-5 h-5" />
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="p-6 m-0">
                  {activity.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No recent activity
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activity.map((event, index) => (
                        <div
                          key={`${event.type}-${event.timestamp}-${index}`}
                          className="flex items-start gap-3 p-3 rounded-lg bg-background"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            event.type === "item_completed" ? "bg-secondary/20 text-secondary" :
                            event.type === "comment_added" ? "bg-primary/20 text-primary" :
                            "bg-accent/20 text-foreground"
                          }`}>
                            {event.type === "item_completed" ? <CheckCircle className="w-4 h-4" /> :
                             event.type === "comment_added" ? <MessageCircle className="w-4 h-4" /> :
                             <Plus className="w-4 h-4" />}
                          </div>
                          
                          <div className="flex-1">
                            <p className="text-sm text-charcoal">
                              <span className="font-medium">{event.user_name}</span>
                              {event.type === "item_completed" && " completed "}
                              {event.type === "item_created" && " added "}
                              {event.type === "comment_added" && " commented on "}
                              <span className="font-medium">{event.item_title}</span>
                            </p>
                            {event.text && (
                              <p className="text-xs text-muted-foreground mt-1">"{event.text}"</p>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(event.timestamp), "MMM d, h:mm a")}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Stats Tab */}
                <TabsContent value="stats" className="p-6 m-0">
                  {stats && (
                    <div className="space-y-6">
                      {/* Progress */}
                      <div className="bg-background rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium text-charcoal">Overall Progress</h3>
                          <span className="text-2xl font-serif font-medium text-primary">
                            {stats.completion_percentage}%
                          </span>
                        </div>
                        <Progress value={stats.completion_percentage} className="h-3" />
                        <p className="text-sm text-muted-foreground mt-2">
                          {stats.completed_items} of {stats.total_items} items completed
                        </p>
                      </div>

                      {/* By Category */}
                      <div className="bg-background rounded-xl p-6">
                        <h3 className="font-medium text-charcoal mb-4">By Category</h3>
                        <div className="space-y-3">
                          {Object.entries(stats.by_category || {}).map(([cat, data]) => {
                            const category = CATEGORIES[cat] || CATEGORIES.experiences;
                            const percent = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
                            
                            return (
                              <div key={cat} className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full ${category.color} bg-opacity-20 flex items-center justify-center`}>
                                  <category.icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-charcoal">{category.label}</span>
                                    <span className="text-muted-foreground">{data.completed}/{data.total}</span>
                                  </div>
                                  <Progress value={percent} className="h-1.5" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* By Member */}
                      <div className="bg-background rounded-xl p-6">
                        <h3 className="font-medium text-charcoal mb-4">Member Contributions</h3>
                        <div className="space-y-3">
                          {Object.entries(stats.by_member || {}).map(([name, data]) => (
                            <div key={name} className="flex items-center justify-between p-3 rounded-lg bg-card">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium text-primary">
                                  {name[0].toUpperCase()}
                                </div>
                                <span className="text-charcoal">{name}</span>
                              </div>
                              <div className="text-right text-sm">
                                <div className="text-charcoal font-medium">{data.created} added</div>
                                <div className="text-muted-foreground">{data.completed} completed</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Members Tab */}
                <TabsContent value="members" className="p-6 m-0">
                  <div className="space-y-3">
                    {group.members?.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 rounded-xl bg-background"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-lg font-medium text-primary">
                            {member.name[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-charcoal dark:text-white flex items-center gap-2">
                              {member.name}
                              {member.role === "admin" && (
                                <Crown className="w-4 h-4 text-accent" />
                              )}
                              {member.id === user.id && (
                                <span className="text-xs text-muted-foreground">(you)</span>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Joined {format(new Date(member.joined_at), "MMM d, yyyy")}
                            </p>
                          </div>
                        </div>
                        
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          member.role === "admin" ? "bg-accent/20 text-foreground" : "bg-muted text-muted-foreground"
                        }`}>
                          {member.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Settings Tab */}
                <TabsContent value="settings" className="p-6 m-0">
                  <div className="space-y-6">
                    {/* Group Info */}
                    <div className="bg-background rounded-xl p-4">
                      <h3 className="font-medium text-charcoal dark:text-white mb-2">Group Information</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Created</span>
                          <span className="text-charcoal dark:text-white">{group.created_at ? format(new Date(group.created_at), "MMM d, yyyy") : "N/A"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Members</span>
                          <span className="text-charcoal dark:text-white">{group.members?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Items</span>
                          <span className="text-charcoal dark:text-white">{items.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Your Role</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${isAdmin ? 'bg-accent/20 text-foreground' : 'bg-muted'}`}>
                            {isAdmin ? 'Admin' : 'Member'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Leave Group */}
                    {!isAdmin && (
                      <div className="bg-background rounded-xl p-4">
                        <h3 className="font-medium text-charcoal dark:text-white mb-2">Leave Group</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          You will lose access to all group items and activities.
                        </p>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" className="w-full border-orange-300 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20">
                              <LogOut className="w-4 h-4 mr-2" />
                              Leave Group
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Leave this group?</AlertDialogTitle>
                              <AlertDialogDescription>
                                You will no longer have access to the group's items and activities. You can rejoin later if invited.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={handleLeaveGroup} className="bg-orange-600 hover:bg-orange-700">
                                Leave Group
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}

                    {/* Delete Group - Admin only */}
                    {isAdmin && (
                      <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4">
                        <h3 className="font-medium text-destructive mb-2 flex items-center gap-2">
                          <Trash2 className="w-4 h-4" />
                          Danger Zone
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Deleting this group will permanently remove all items, photos, and activities. This action cannot be undone.
                        </p>
                        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="destructive" 
                              className="w-full"
                              data-testid="delete-group-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Group
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{group.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the group, all {items.length} items, and remove all {group.members?.length} members. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={handleDeleteGroup}
                                disabled={deleting}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                {deleting ? "Deleting..." : "Delete Forever"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </>
            )}
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default GroupView;
